import { useCallback, useEffect, useMemo, useState } from "react";
import { useToasts } from "../../contexts/toasts";
import { EffortType } from "../../types/Entities";
import { Entities } from "../../api/Entities";
import { Alert, Button, Container, Modal } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faExclamationTriangle,
  faPen,
  faPlus,
  faSave,
  faTrash,
} from "@fortawesome/free-solid-svg-icons";
import { TailSpin } from "react-loader-spinner";
import {
  ConfigProvider,
  Empty,
  Form,
  GetProp,
  Input,
  Space,
  Table,
} from "antd";
import IconButton from "../../common/IconButton";
import { PagedResults } from "../../types/generics";
import AppSearch from "../../common/AppSearch";
import { debounce } from "lodash";
import ListPageHeader from "../../common/ListPageHeader";
import ListPanel from "../../common/ListPanel";

export default function EffortTypesList() {
  const { showToast } = useToasts();
  const [effortTypes, setEffortTypes] = useState<EffortType[]>();
  const [error, setError] = useState(false);
  const [totalEffortTypes, setTotalEffortTypes] = useState<number>();
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState<{
    freeText: string;
    pageSize: number;
    currentPage: number;
  }>({
    freeText: "",
    pageSize: 20,
    currentPage: 1,
  });
  const [addModal, setAddModal] = useState(false);
  const [deleteModal, setDeleteModal] = useState(false);
  const [form] = Form.useForm();
  const [selectedItem, setSelectedItem] = useState<EffortType>();

  // 🔹 Inizializza lista EffortType
  const initEffortTypes = useCallback(() => {
    const urlParams = new URLSearchParams();
    urlParams.append("page", `${filters.currentPage}`);
    urlParams.append("size", `${filters.pageSize}`);

    if (filters.freeText) urlParams.append("freeText", `${filters.freeText}`);

    Entities.getPaginatedEffortTypes(urlParams).then(
      ({ data }) => {
        setEffortTypes((data as PagedResults<EffortType>).items);
        setTotalEffortTypes((data as PagedResults<EffortType>).totalItems);
      },
      () => {
        showToast(
          "efforttypes-error",
          "Impossibile ottenere la lista dei tipi di Effort",
          "",
          "danger"
        );
        setEffortTypes([]);
        setError(true);
      }
    );
  }, [filters, showToast]);

  const reset = useCallback(() => {
    setError(false);
    setEffortTypes(undefined);
    initEffortTypes();
  }, [initEffortTypes]);

  useEffect(() => {
    reset();
  }, [filters, reset]);

  const handlePageChange = useCallback((page: number) => {
    setFilters((prev) => ({ ...prev, currentPage: page }));
  }, []);

  const handlePageSizeChange = useCallback((_current: number, size: number) => {
    setFilters((prev) => ({ ...prev, pageSize: size, currentPage: 1 }));
  }, []);

  const handleEditClick = useCallback(
    (item?: EffortType) => () => {
      setSelectedItem(item);
      if (item) form.setFieldsValue({ effortType: item.effortType });
      setAddModal(true);
    },
    [form]
  );

  const handleDeleteClick = useCallback(
    (item: EffortType) => () => {
      setSelectedItem(item);
      setDeleteModal(true);
    },
    []
  );

  const handleFilterChange = useCallback((text: string) => {
    setFilters((previous) => ({
      ...previous,
      currentPage: 1,
      freeText: text,
    }));
  }, []);

  const debounceSearchChange = useMemo(
    () => debounce(handleFilterChange, 500),
    [handleFilterChange]
  );

  useEffect(() => {
    return () => {
      debounceSearchChange.cancel();
    };
  }, [debounceSearchChange]);

  const renderToolbar = useCallback(() => {
    return (
      <ListPageHeader
        title="Effort Type"
        count={totalEffortTypes}
        actions={
          <button onClick={handleEditClick()} className="button-primary">
            <FontAwesomeIcon icon={faPlus} />
            <span className="d-none d-sm-inline ms-2">
              Aggiungi Effort Type
            </span>
          </button>
        }
      >
        <AppSearch
          placeholder="Cerca per nome..."
          onChange={debounceSearchChange}
        />
      </ListPageHeader>
    );
  }, [handleEditClick, debounceSearchChange, totalEffortTypes]);

  const renderEmpty: GetProp<typeof ConfigProvider, "renderEmpty"> = () => (
    <Empty
      image={Empty.PRESENTED_IMAGE_SIMPLE}
      description="Nessun Effort Type"
    />
  );

  const renderTable = useCallback(() => {
    if (error)
      return (
        <Alert variant="danger" className="py-2">
          <FontAwesomeIcon icon={faExclamationTriangle} />
          <span className="ms-2">
            C'è stato un errore, per favore riprova più tardi.
          </span>
        </Alert>
      );

    return (
      <Table
        size="middle"
        bordered
        dataSource={effortTypes}
        rowKey="id"
        locale={{ emptyText: renderEmpty() }}
        pagination={{
          pageSize: filters.pageSize,
          current: filters.currentPage,
          onChange: handlePageChange,
          onShowSizeChange: handlePageSizeChange,
          total: totalEffortTypes,
        }}
      >
        <Table.Column
          key="effortType"
          dataIndex="effortType"
          title="Effort Type"
          className="text-center col-pattern"
        />
        <Table.Column
          key="actions"
          title="Azioni"
          className="text-center col-pattern"
          render={(_value, record: EffortType) => (
            <Space size={1}>
              <IconButton tooltip="Modifica" onClick={handleEditClick(record)}>
                <FontAwesomeIcon icon={faPen} />
              </IconButton>
              <IconButton
                tooltip="Elimina"
                className="text-danger"
                onClick={handleDeleteClick(record)}
              >
                <FontAwesomeIcon icon={faTrash} />
              </IconButton>
            </Space>
          )}
        />
      </Table>
    );
  }, [
    effortTypes,
    error,
    filters,
    totalEffortTypes,
    handlePageChange,
    handlePageSizeChange,
    handleEditClick,
    handleDeleteClick,
  ]);

  const renderAsyncContent = useCallback(() => {
    return <ListPanel loading={!effortTypes}>{renderTable()}</ListPanel>;
  }, [effortTypes, renderTable]);

  const handleAddModalClose = useCallback(() => {
    setAddModal(false);
    form.resetFields();
    setSelectedItem(undefined);
  }, [form]);

  const handleDeleteModalClose = useCallback(() => {
    setDeleteModal(false);
    setSelectedItem(undefined);
  }, []);

  const handleSubmit = useCallback(
    (values: any) => {
      setLoading(true);
      const body = { ...values };
      const promise = selectedItem
        ? Entities.updateEffortType(selectedItem.id!, body)
        : Entities.createEffortType(body);

      promise.then(
        () => {
          setLoading(false);
          showToast(
            "efforttype-success",
            `Effort Type ${
              selectedItem ? "modificato" : "creato"
            } correttamente`,
            undefined,
            "success"
          );
          handleAddModalClose();
          reset();
        },
        () => {
          setLoading(false);
          showToast(
            "efforttype-error",
            `Errore nella ${
              selectedItem ? "modifica" : "creazione"
            } dell’Effort Type`,
            undefined,
            "danger"
          );
        }
      );
    },
    [selectedItem, showToast, handleAddModalClose, reset]
  );

  const handleDelete = useCallback(() => {
    setLoading(true);
    Entities.deleteEffortType(selectedItem!.id!).then(
      () => {
        setLoading(false);
        handleDeleteModalClose();
        reset();
        showToast(
          "delete-efforttype-success",
          "Effort Type eliminato correttamente",
          undefined,
          "success"
        );
      },
      () => {
        setLoading(false);
        showToast(
          "delete-efforttype-error",
          "Errore nella cancellazione dell’Effort Type, per favore riprova",
          undefined,
          "danger"
        );
      }
    );
  }, [selectedItem, showToast, handleDeleteModalClose, reset]);

  return (
    <Container fluid="md" className="mt-5 list-page-container">
      {renderToolbar()}
      {renderAsyncContent()}

      {/* MODALE AGGIUNTA / MODIFICA */}
      <Modal show={addModal} onHide={handleAddModalClose}>
        <Modal.Header closeButton>
          <Modal.Title>
            {selectedItem ? "Modifica" : "Nuovo"} Effort Type
          </Modal.Title>
        </Modal.Header>
        <Form form={form} onFinish={handleSubmit}>
          <Modal.Body>
            <Form.Item
              label="Effort Type"
              name="effortType"
              rules={[{ required: true, message: "Campo obbligatorio" }]}
            >
              <Input className="w-100" />
            </Form.Item>
          </Modal.Body>
          <Modal.Footer className="justify-content-between">
            <Button
              onClick={handleAddModalClose}
              variant="light"
              size="sm"
              className="border me-2"
            >
              Annulla
            </Button>
            <Button variant="success" size="sm" type="submit">
              {loading ? (
                <TailSpin width={15} height={15} color="#9c1419" />
              ) : (
                <>
                  <FontAwesomeIcon icon={faSave} />
                  <span className="ms-2">Salva</span>
                </>
              )}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* MODALE ELIMINAZIONE */}
      <Modal show={deleteModal} onHide={handleDeleteModalClose}>
        <Modal.Header closeButton>
          <Modal.Title>Elimina Effort Type</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>
            Sei sicuro di voler cancellare l’Effort Type{" "}
            <strong>{selectedItem?.effortType}</strong>?
          </p>
          <Alert variant="warning">
            <FontAwesomeIcon icon={faExclamationTriangle} />
            <span className="ms-2">
              Questa azione non può essere annullata.
            </span>
          </Alert>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="light"
            size="sm"
            className="border"
            onClick={handleDeleteModalClose}
          >
            Chiudi
          </Button>
          <Button variant="danger" size="sm" onClick={handleDelete}>
            {loading ? (
              <TailSpin width={20} height={20} />
            ) : (
              <span>Cancella</span>
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
}
